import { useState, useCallback } from 'react';
import axios from 'axios';
import { JournalEntry, Pagination, PeriodStatus } from '../types';
import { API_BASE, DEFAULT_PAGE_LIMIT } from '../constants';
import { SortField, Order } from '../types';

export interface JournalEntriesState {
  entries: JournalEntry[];
  tableLoading: boolean;
  pagination: Pagination | null;
  periodStatus: PeriodStatus | null;
  fetchEntries: (page: number) => Promise<void>;
}

/**
 * Загружает страницу записей журнала с сервера.
 * Требует pharmacyGuid — идентификатор аптеки пользователя.
 * Поддерживает пагинацию, сортировку и произвольные фильтры.
 */
export function useJournalEntries(
  pharmacyGuid: string,
  filterParams: Record<string, string>,
  orderBy: SortField,
  order: Order,
): JournalEntriesState {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [periodStatus, setPeriodStatus] = useState<PeriodStatus | null>(null);

  const fetchEntries = useCallback(async (currentPage: number) => {
    if (!pharmacyGuid) return;
    setTableLoading(true);
    try {
      const resp = await axios.get(`${API_BASE}/journal-user`, {
        params: {
          pharmacyGuid,
          ...filterParams,
          orderBy,
          order,
          page: currentPage,
          limit: DEFAULT_PAGE_LIMIT,
        },
      });
      if (resp.data?.success) {
        setEntries(resp.data.data || []);
        setPagination(resp.data.pagination || null);
        if (resp.data.periodStatus) {
          setPeriodStatus(resp.data.periodStatus);
        }
      }
    } catch (e) {
      console.error('Journal fetch error:', e);
    } finally {
      setTableLoading(false);
    }
  }, [pharmacyGuid, filterParams, orderBy, order]);

  return { entries, tableLoading, pagination, periodStatus, fetchEntries };
}
